import * as CSS from 'csstype'
import * as flatbuffers from 'flatbuffers'
import {validate} from 'uuid'

import {ErrorID, UserFacingError, Uuid} from '.'
import {HexColor, UserLinkMap} from './types'

interface LengthCheckParams {
  length: number
  error: ErrorID
}

/**
 * Throws the associated error if `toValidate` is longer than max.length or shorter than min.length
 */
export function validateStringLength(
  toValidate: string,
  {max, min}: {max?: LengthCheckParams; min?: LengthCheckParams}
) {
  if (max && toValidate.length > max.length) {
    throw new UserFacingError(max.error)
  }

  if (min && toValidate.length < min.length) {
    throw new UserFacingError(min.error)
  }
}

/**
 * Returns true if its a defined non-whitespace character.
 */
function isCharacter(text: string | undefined): boolean {
  return !!(text && !text?.match(/\s/))
}

/**
 * Get the word at or right before the provided position in the string.
 *
 * @param text The text to search for words near the cursor.
 * @param position The position at or before to find a word.
 * @returns [the word found, the position found] or null if none found.
 */
export function getWordAtPosition(text: string, position: number): [word: string | null, startPos: number | null] {
  position = Math.min(text.length - 1, Math.max(0, position))

  // Look for a character where we are or behind us.
  if (!isCharacter(text[position]) && !isCharacter(text[position - 1])) {
    return [null, null]
  }

  // Now look for the first non-whitespace character in this group.
  while (isCharacter(text[position - 1])) {
    position--
  }
  const startPos = position

  let returnWord = ''
  do {
    returnWord += text[position]
    position++
  } while (isCharacter(text[position]))

  return [returnWord, startPos]
}

// Ported from Ginger-Labs/glut uuid.ts — base-64 encode/decode for UUID bytes
// chars rationale at https://github.com/dominictarr/d64

const uuidHexToByte: {[key: string]: number} = {}
for (let i = 0; i < 256; i++) {
  uuidHexToByte[(i + 0x100).toString(16).substring(1)] = i
}

function parseUUIDv4(str: string): Uint8Array {
  const u8s = new Uint8Array(16)
  let ii = 0
  str.toLowerCase().replace(/[0-9a-f]{2}/g, (oct) => {
    if (ii < 16) {
      u8s[ii++] = uuidHexToByte[oct] ?? 0
    }
    return ''
  })
  while (ii < 16) {
    u8s[ii++] = 0
  }
  return u8s
}

function encodeBytes(source: Uint8Array, chars: string, base: number): string {
  const digits: number[] = [0]
  for (let i = 0; i < source.length; ++i) {
    let carry: number = source[i]!
    for (let j = 0; j < digits.length; ++j) {
      carry += digits[j]! << 8
      digits[j] = carry % base
      carry = (carry / base) | 0
    }
    while (carry > 0) {
      digits.push(carry % base)
      carry = (carry / base) | 0
    }
  }
  let str = ''
  for (let k = 0; source[k] === 0 && k < source.length - 1; ++k) {
    str += chars[0]
  }
  for (let q = digits.length - 1; q >= 0; --q) {
    str += chars[digits[q]!]
  }
  return str
}

const friendlyUUIDChars = '~0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz'
const friendlyUUIDBase = friendlyUUIDChars.length
const friendlyUUIDCharValues: Record<string, number> = {'.': 0} // legacy leader was '.'
for (let i = 0; i < friendlyUUIDChars.length; i++) {
  friendlyUUIDCharValues[friendlyUUIDChars.charAt(i)] = i
}

function decodeFriendlyUUID(str: string): Uint8Array {
  const bytes: number[] = []
  for (let i = 0; i < str.length; i++) {
    const mapped = friendlyUUIDCharValues[str[i]!]
    if (mapped === undefined) {
      throw new Error('invalid base64 encoding')
    }
    let carry: number = mapped
    for (let j = 0; j < bytes.length; ++j) {
      carry += bytes[j]! * friendlyUUIDBase
      bytes[j] = carry & 0xff
      carry >>= 8
    }
    while (carry > 0) {
      bytes.push(carry & 0xff)
      carry >>= 8
    }
  }
  for (let k = 0; (str[k] === '~' || str[k] === '.') && k < str.length; ++k) {
    bytes.push(0)
  }
  return new Uint8Array(bytes.reverse())
}

function isFriendlyUUIDLength(str: string): boolean {
  return str.length >= 16 && str.length <= 22
}

export function getFriendlyUUID(id: string): string {
  if (isFriendlyUUIDLength(id)) return encodeBytes(decodeFriendlyUUID(id), friendlyUUIDChars, friendlyUUIDBase)
  if (id.length === 36) return encodeBytes(parseUUIDv4(id), friendlyUUIDChars, friendlyUUIDBase)
  throw new Error('Cannot deserialize to id: ' + id)
}

/** The lowercase hyphenated UUID that a friendly UUID encodes, or null when the string is not one. */
export function uuidFromFriendlyUUID(friendly: string): string | null {
  if (!isFriendlyUUIDLength(friendly)) return null
  let bytes: Uint8Array
  try {
    bytes = decodeFriendlyUUID(friendly)
  } catch {
    return null
  }
  if (bytes.length !== 16) return null
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Like getFriendlyUUID but uses a pure alphanumeric alphabet (no ~ or _).
// Intended for user-facing codes where special characters are unwanted.
const alphanumericChars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
export function getFriendlyAlphanumericUUID(id: string): string {
  if (id.length !== 36) {
    throw new Error(`Expected 36-char UUIDv4, got: ${id}`)
  }
  return encodeBytes(parseUUIDv4(id), alphanumericChars, alphanumericChars.length)
}

function replacementRegex(delimiter: string) {
  return new RegExp(`[^0-9a-z${delimiter}]`, 'g')
}

export function profilesToUserLinkMap(
  profiles: {
    screenname?: string
    user: {
      id?: string
    }
    avatarUpdatedAt?: Date
  }[]
): UserLinkMap {
  return profiles.reduce<UserLinkMap>((screennameAndUserIdPairs, profile) => {
    if (profile.screenname && profile.user.id) {
      screennameAndUserIdPairs[profile.screenname] = profile.avatarUpdatedAt
        ? {userId: profile.user.id, avatarUpdatedString: JSON.stringify(profile.avatarUpdatedAt)}
        : {userId: profile.user.id}
    }
    return screennameAndUserIdPairs
  }, {})
}

/**
 * Take a title string and turn it into a kebab-case or snake_case case ID.
 * Removes all non-alphanumeric characters and replaces spaces with hyphens.
 * Lowercases all letters.
 *
 * @param source The string to convert to a kebab-case id.
 * @param maxLength (optional) Maximum length of the string.
 * @param delimiter (optional) Underscore or hyphen as a string. Defaults to hyphen (`-`). Replaces whitespace in the source.
 */
export function generateDelimitedId(source: string, delimiter: '-' | '_', maxLength?: number) {
  const allBesidesAlphanumericAndDelimiter = replacementRegex(delimiter)
  const replaceDelimiters = delimiter === '-' ? /[\s_]+/g : /[\s-]+/g
  const delimited = source
    .toLowerCase()
    .replace(replaceDelimiters, delimiter)
    .replace(allBesidesAlphanumericAndDelimiter, '')
  if (maxLength) {
    return delimited.slice(0, maxLength)
  } else {
    return delimited
  }
}

export function generateKebabCaseId(source: string, maxLength?: number) {
  return generateDelimitedId(source, '-', maxLength)
}

// topics look like 'Topic One', and tags are formatted as `topic_one`
export function generateSnakeCase(topic: string, maxLength?: number) {
  return generateDelimitedId(topic, '_', maxLength)
}

export const ValidProtocols = ['http://', 'https://'] as const
export type Protocol = (typeof ValidProtocols)[number]

export function isProtocol(proto: any): asserts proto is Protocol {
  return proto && ValidProtocols.includes(proto)
}

export function removeUndefinedValues<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.keys(obj).reduce((acc, key) => {
    const typedKey = key as keyof T
    if (obj[typedKey]) {
      ;(acc as any)[typedKey] = obj[typedKey]
    }
    return acc
  }, {} as Partial<T>)
}

// https://stackoverflow.com/a/64007175/91713
export function hasEmojis(str: string) {
  return /\p{Extended_Pictographic}/u.test(str)
}

export function filterString(str: string, fn: (char: string) => boolean) {
  const output = []
  for (const char of str) {
    if (fn(char)) {
      output.push(char)
    }
  }

  return output.join('')
}

export function filterEmojis(str: string) {
  return filterString(str, (char) => !hasEmojis(char))
}

/**
 * temporary code to enable adding timing statistics to long-running job operations
 */
export async function timed<T>(fn: () => Promise<T>) {
  const start = Date.now()
  const data = await fn()
  const end = Date.now()

  const timeMs = end - start

  return {
    data,
    timeMs
  }
}

export function sortObjectEntries<T extends Record<string, any>>(input: T): T {
  const sortedEntries = Object.entries(input).sort(([a], [b]) => a.localeCompare(b, 'en'))
  return Object.fromEntries(sortedEntries) as T
}

export function hexToRgbValues(color: HexColor | CSS.Property.Color): {r: number; g: number; b: number} {
  // Remove alpha value from color string if present
  const inputWithoutAlpha = color.substring(0, 7)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(inputWithoutAlpha)
  if (!result || result.length < 4) {
    return {r: 0, g: 0, b: 0}
  }

  const r = parseInt(result[1]!, 16)
  const g = parseInt(result[2]!, 16)
  const b = parseInt(result[3]!, 16)
  return {r, g, b}
}

/**
 * Adapted from: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
 * @param hexColor Color in hex values (format: #xxxxxx)
 * @param alpha Alpha transparency to be applied (if any).
 * @returns A CSS Color property in the format rgb(...) or rgba(...)
 */
export function hexToRgb(hexColor: CSS.Property.Color, alpha?: number): CSS.Property.Color {
  const {r, g, b} = hexToRgbValues(hexColor)

  if (typeof alpha === 'number') {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  } else {
    return `rgb(${r}, ${g}, ${b})`
  }
}

/**
 * Also adapted from: https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
 * @param r Color component value in range 0...255
 * @param g Color component value in range 0...255
 * @param b Color component value in range 0...255
 * @returns A CSS Color property in the hex format #xxxxxx
 */
export function rgbToHex(r: number, g: number, b: number, a?: number): HexColor {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}${a !== undefined ? componentToHex(a) : ''}`
}

function componentToHex(c: number) {
  const hex = c.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}

function isHexColor(value: unknown): value is HexColor {
  return typeof value === 'string' && value.startsWith('#')
}

export function validateHexColor(value: unknown): HexColor | undefined {
  return isHexColor(value) ? value : undefined
}

export function sum(arr: number[]) {
  return arr.reduce((cumulative, value) => cumulative + value, 0)
}

export function arraysEqual(a: any[], b: any[]) {
  if (a === b) return true
  if (a === null || b === null) return false
  if (a.length !== b.length) return false

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export function toArray<T>(val: T | T[]) {
  if (Array.isArray(val)) {
    return val
  } else {
    return [val]
  }
}

/**
 * Sorts the properties and sub-properties of the object in a consistent way, regardless
 * of how they were assigned.
 */
export function sortProperties<T>(obj: T) {
  if (typeof obj !== 'object') {
    return obj
  }

  const record = obj as Record<string, any>
  const keys = Object.keys(record).sort()
  return keys.reduce((acc, k) => {
    record[k] = sortProperties(record[k])
    return acc
  }, {} as T)
}

export function arrayOfRange(start: number, stop: number): number[] {
  const length = stop - start > 0 ? Math.floor(stop - start) : 0
  if (length > 0xffffffff) throw new RangeError('Invalid array length')
  const out: number[] = []
  for (let i = 0; i < length; i++) out.push(start + i)
  return out
}

export function arrayOfRangeStopInclusive(start: number, stop: number): number[] {
  return arrayOfRange(start, stop + 1)
}

// start: The starting value to use for the sequence. If the sequence contains any values, the first one is start.
// end: An end value to limit the sequence. end is only an element of the resulting sequence if `includeEnd` is true.
// step: The amount to step by with each iteration. A positive step iterates upward; a negative step iterates downward.
// includeEnd: Whether to include the range end, whether or not it fits the step.
export function arrayStride(from: number, to: number, step: number, includeEnd: boolean = false) {
  const arr: number[] = []
  if (from > to && step > 0) return arr
  if (from < to && step < 0) return arr
  if (from !== to && step === 0) return arr
  let val = from
  let condition = step > 0 ? val < to : val > to
  while (condition) {
    arr.push(val)
    val = val + step
    condition = step > 0 ? val < to : val > to
  }
  if (includeEnd) {
    if (arr[arr.length - 1] !== to) arr.push(to)
  }
  return arr
}

export function toUuidOffset(builder: flatbuffers.Builder, uuid: string): number {
  const {low, high} = toLowHighNumbers(uuid)

  return Uuid.createUuid(builder, low, high)
}

// this byte magic was taken from (i tested it and it works):
// https://stackoverflow.com/questions/75387792/javascript-equivalent-of-javas-uuid-class

export function toLowHighNumbers(uuid: string) {
  const components = uuid.split('-')
  if (components.length !== 5) {
    throw new Error(`Invalid UUID string: ${uuid}`)
  }

  for (let index = 0; index < 5; index++) {
    components[index] = `0x${components[index]}`
  }

  let high = BigInt(Number.parseInt(components[0]!, 16))
  high <<= 16n
  high |= BigInt(Number.parseInt(components[1]!, 16))
  high <<= 16n
  high |= BigInt(Number.parseInt(components[2]!, 16))

  let low = BigInt(Number.parseInt(components[3]!, 16))
  low <<= 48n
  low |= BigInt(Number.parseInt(components[4]!, 16))

  return {
    low,
    high
  }
}

export function toUuidString(lsb: bigint, msb: bigint) {
  return `${digits(msb >> 32n, 8n)}-${digits(msb >> 16n, 4n)}-${digits(msb, 4n)}-${digits(lsb >> 48n, 4n)}-${digits(
    lsb,
    12n
  )}`
}

function digits(value: bigint, ds: bigint) {
  const hi = 1n << (ds * 4n)

  return (hi | (value & (hi - 1n))).toString(16).slice(1)
}

function lookupInspectSymbol(): symbol | undefined {
  if (typeof Symbol !== 'undefined' && typeof (Symbol as any).for === 'function') {
    return (Symbol as any).for('nodejs.util.inspect.custom')
  }
  return undefined
}

/**
 * This function allows for an object to conditionally attach custom debug string generators. It will only add it to the object when the code is run in a node.js environment.
 *
 * @argument proto The prototype of the object you would like to attach the descriptor to. You **MUST** pass in the object prototype here, not an instance.
 *
 * @argument inspectCallback The callback function to produce the debug string.
 */
const inspectSymbol = lookupInspectSymbol()
export function attachCustomInspectCallback<T>(proto: {}, callback: (this: T) => string) {
  if (inspectSymbol) {
    Object.defineProperty(proto, inspectSymbol, {
      value: callback
    })
  }
}

export function setsAreEqual<T>(setA: Set<T>, setB: Set<T>): boolean {
  if (setA.size !== setB.size) {
    return false
  }

  for (const item of setA) {
    if (!setB.has(item)) {
      return false
    }
  }

  return true
}

export function isArrayEmpty<T>(arr?: Array<T> | null): boolean {
  if (!arr) {
    return true
  }

  return arr.length === 0
}

// Removes all null and undefined values from an array
export function compact<T>(arr: Array<T | undefined | null>): T[] {
  return arr.filter<T>((value?: T | null): value is T => !!value)
}

// Takes in any number of optional nullable arrays as params and returns one array with undefined and null values removed
export function compactConcat<T>(...arrs: (Array<T | undefined | null> | undefined | null)[]): T[] {
  const arr = compact(arrs).reduce((collection, current) => collection.concat(current), [])
  return compact(arr)
}

// Takes in a nullable array and returns an array with all duplicates removed
export function dedupeArray<T>(arr: Array<T> | null | undefined) {
  if (arr === undefined || arr === null || arr.length === 0) {
    return []
  }

  return Array.from(new Set(arr))
}

// Like dedupeArray, but adds a hash function to map values that are logically duplicates
// but not strictly Javascript Equal - For example `dedupeArrayBy(events, (e) => e.id)`
export function dedupeArrayBy<Value, Key>(
  arr: Array<Value> | null | undefined,
  hash: (value: Value) => Key
): Array<Value> {
  if (arr === undefined || arr === null || arr.length === 0) {
    return []
  }

  const m = new Map<Key, Value>()
  arr.forEach((value) => {
    m.set(hash(value), value)
  })
  return Array.from(m.values())
}

/**
 * Shuffles an array in place using the fisher-yates algorithm
 * Source: https://www.geeksforgeeks.org/how-to-shuffle-an-array-using-javascript/
 * @argument array The array to shuffle
 * @argument randomFn Optional random number generator function that returns a value between 0 and 1. Defaults to Math.random
 * @returns a reference to the array, now shuffled, that was passed in
 */
export const shuffleArray = <T>(array: T[], randomFn: () => number = Math.random): T[] => {
  for (let i = array.length - 1; i > 0; i--) {
    // Generate random index
    const j = Math.floor(randomFn() * (i + 1))

    // Swap elements at indices i and j
    const temp = array[i]!
    array[i] = array[j]!
    array[j] = temp
  }
  return array
}

// Uses fisher-yates style swapping to pop random elements from an array without resorting to splicing
export function popRandom<T>(arr: T[], k: number): T[] {
  const result: T[] = []
  for (let i = 0; i < k; i++) {
    const j = Math.floor(Math.random() * arr.length)
    result.push(arr[j] as T)
    arr[j] = arr[arr.length - 1] as T
    arr.pop()
  }
  return result
}

/**
 * This function takes an arbitrary number of lists and combines their columns together
 *
 * @example Two lists of the same length
 *
 * ``` typescript
 * const a = [1,2,3]
 * const b = ["a","b","c"]
 *
 * // Returns [[1, "a"], [2, "b"], [3, "c"]]
 * zip(a,b)
 * ```
 *
 * @example Two lists of different length
 *
 * ``` typescript
 * const a = arrayOfRange(0, Number.MAX_VALUE)
 * const b = ["a","b","c"]
 *
 * // zip will only return a list as long as the shortest of the two you pass to it
 * // Returns [[1, "a"], [2, "b"], [3, "c"]]
 * zip(a,b)
 * ```
 *
 * @example three different lists of the same length
 * ``` typescript
 * const a = [1, 2, 3]
 * const b = ["a","b","c"]
 * const c = [true, false, true]
 *
 * // zip will return a columnar slice of each array passed in
 * // Returns [[1, "a", true], [2, "b", false], [3, "c", true]]
 * zip(a,b,c)
 * ```
 *
 *
 * @argument args the arrays which you would like to zip
 */
export function zip<T extends unknown[][]>(...args: T): ZipperArgs<T>[] {
  const minLength = Math.min(...args.map((arg) => arg.length))
  const outArr: ZipperArgs<T>[] = Array.from({length: minLength})
  for (let i = 0; i < minLength; i++) {
    outArr[i] = args.map((arr) => arr[i]!) as ZipperArgs<T>
  }
  return outArr
}

type ZipperArgs<Lists> = {
  [K in keyof Lists]: Lists[K] extends (infer ListT)[] ? ListT : never
}

/**
 * Similar to {@link zip} but will zip all items even if some lists are longer than others. As a concequence, the type guarantees are not as strong

** @example three different lists of different lengths
 * ``` typescript
 * const a = [1, 2, 3]
 * const b = ["a", "b", "c", "d", "e"]
 * const c = [true, false, true, false]
 *
 * // zip will return a columnar slice of each array passed in
 * // Returns [[1, "a", true], [2, "b", false], [3, "c", true], ["d", false], ["e"]]
 * zipUnsafe(a,b,c)
 * ```

*
 */
export function zipUnsafe<T extends unknown[][]>(...args: T) {
  const maxLength = Math.max(...args.map((arg) => arg.length))
  const outArr: [T[number][number]][] = Array.from({length: maxLength})
  for (let i = 0; i < maxLength; i++) {
    outArr[i] = args.map((arr) => arr[i]).filter((arg) => arg !== undefined) as [T[number][number]]
  }
  return outArr
}

const FILE_EXTENSION_REGEX = /\.[0-9a-zA-Z]+$/

export function getFileExtension(filename: string): string | null {
  const match = filename.match(FILE_EXTENSION_REGEX)
  if (!match) {
    return null
  } else {
    return match[0].toLowerCase()
  }
}

export function getFilenameWithoutExtension(filename: string): string {
  return filename.replace(FILE_EXTENSION_REGEX, '')
}

export function escapeCharacters(str: string): string {
  return str.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&') // Escape any regexp special characters https://stackoverflow.com/a/3561711
}

// Generic linear interpolation formula for when you want a value along a line between two points
export function linearInterpolateYValue(atX: number, x1: number, y1: number, x2: number, y2: number) {
  return y1 + ((atX - x1) * (y2 - y1)) / (x2 - x1)
}

// Generic function to ensure value is no greater or less than the input values
export function applyBounds(value: number, maximum: number, minimum: number) {
  return Math.max(Math.min(value, maximum), minimum)
}

export function cartesian<A, B>(coll1: A[], coll2: B[]): [A, B][] {
  return coll1.flatMap((c1) => coll2.map((c2) => [c1, c2] as [A, B]))
}

export function allSubsets<T>(coll: T[], {includeEmpty = true}: {includeEmpty?: boolean} = {}): T[][] {
  const subsets = coll.reduce<T[][]>((acc, x) => [...acc, ...acc.map((s) => [...s, x])], [[]])
  return includeEmpty ? subsets : subsets.filter((s) => s.length > 0)
}

/*
 * Returns every way to assign each item to one of the states — |states|^|items| mappings.
 *
 * allMappings([a, b], [1, 2, 3]) → [
 *   [[a, 1], [b, 1]],
 *   [[a, 1], [b, 2]],
 *   [[a, 1], [b, 3]],
 *   [[a, 2], [b, 1]],
 *   [[a, 2], [b, 2]],
 *   [[a, 2], [b, 3]],
 *   [[a, 3], [b, 1]],
 *   [[a, 3], [b, 2]],
 *   [[a, 3], [b, 3]]
 * ]
 */
export function allMappings<T, U>(items: T[], states: U[]): [T, U][][] {
  return items.reduce<[T, U][][]>(
    (acc, item) => acc.flatMap((partial) => states.map((state) => [...partial, [item, state] as [T, U]])),
    [[]]
  )
}

export function validNumberOrZero(num: number | null | undefined) {
  if (num === undefined || num === null || isNaN(num)) {
    return 0
  }

  return num
}

export type StringRecord<A> = Record<string, A>

export function groupBy<A>(f: (a: A) => string | number): (as: A[]) => StringRecord<A[]>
export function groupBy<A>(as: A[], f: (a: A) => string | number): StringRecord<A[]>
export function groupBy<A>(
  asOrF: A[] | ((a: A) => string | number),
  f?: (a: A) => string | number
): StringRecord<A[]> | ((as: A[]) => StringRecord<A[]>) {
  if (typeof asOrF === 'function') {
    const fn = asOrF
    return (as: A[]) => groupBy(as, fn)
  }
  const result: StringRecord<A[]> = {}
  for (const a of asOrF) {
    const key = String(f!(a))
    ;(result[key] ??= []).push(a)
  }
  return result
}

export function keyBy<A>(f: (a: A) => string | number): (as: A[]) => StringRecord<A>
export function keyBy<A>(as: A[], f: (a: A) => string | number): StringRecord<A>
export function keyBy<A>(
  asOrF: A[] | ((a: A) => string | number),
  f?: (a: A) => string | number
): StringRecord<A> | ((as: A[]) => StringRecord<A>) {
  if (typeof asOrF === 'function') {
    const fn = asOrF
    return (as: A[]) => keyBy(as, fn)
  }
  const result: StringRecord<A> = {}
  for (const a of asOrF) {
    result[String(f!(a))] = a
  }
  return result
}

// UUIDs contain reliably random bytes so we can safely derive a random number from a v4 uuid using this approach
export function getRandomFromUUID(uuid: string): number {
  // Remove hyphens and take first 8 hex chars (32 bits)
  const hex = uuid.replaceAll('-', '').substring(0, 8)
  const num = parseInt(hex, 16)

  // Divide by max 32-bit+1 value to get a number between 0.0 and 1.0
  return num / 0x100000000
}

export function validateAndLowercaseUuid(uuidString?: string | null) {
  if (uuidString && validate(uuidString)) {
    return uuidString.toLowerCase()
  }

  return undefined
}

// Only works for JSON-like objects
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || a === undefined || b === null || b === undefined) return a === b

  const typeA = typeof a
  const typeB = typeof b
  if (typeA !== typeB) return false

  if (typeA === 'object') {
    const isArrayA = Array.isArray(a)
    const isArrayB = Array.isArray(b)
    if (isArrayA !== isArrayB) return false

    if (isArrayA) {
      const arrayA = a as unknown[]
      const arrayB = b as unknown[]
      if (arrayA.length !== arrayB.length) return false
      return arrayA.every((item, i) => deepEqual(item, arrayB[i]))
    }

    const keysA = Object.keys(a as Record<string, unknown>)
    const keysB = Object.keys(b as Record<string, unknown>)
    if (keysA.length !== keysB.length) return false

    return keysA.every((key) => deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]))
  }

  return false
}
